import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity, useRealtimeSubscription } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as financeApplicationActions from '@redux/modules/financeApplication'

import defaultFormState, { requiredFields } from '@models/financeApplication'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'

import mergeAndStringifyEntityObjects from '@functions/entity'
import getApplicationStatus from '@functions/financeApplication'

import type { CampaignModel, FinanceApplicationModel, FinanceApplicationRequestOptions } from '@models/types'

import PageContext from '@contexts/pageContext'
import RealtimeContext from '@contexts/realtimeContext'

import useCurrentUser from './useCurrentUser'

const stringifyObjectKeys: (keyof FinanceApplicationModel)[] = ['data']

type CreateOrUpdateFinanceApplicationParams = {
  data: { payload: FinanceApplicationModel },
  dispatch: AppDispatch,
}

const createOrUpdateFinanceApplication = (params: CreateOrUpdateFinanceApplicationParams) => {
  const { data: { payload }, dispatch } = params
  const { approved, data: financeAppData } = payload

  const { createOrUpdateFinanceApplicationKey: createOrUpdateFn } = financeApplicationActions

  const declined = digObject(financeAppData, 'declined', false)

  const key = declined ? 'data' : 'approved'
  const value = declined ? { declined } : approved

  return dispatch(createOrUpdateFn({
    // @ts-expect-error TS2322
    incomingFinanceApplication: payload, key, value,
  }))
}

type CreateFinanceApplicationParams = {
  dispatch: AppDispatch,
  financeApplicationParams: Partial<FinanceApplicationModel>,
  requestOptions?: FinanceApplicationRequestOptions,
}

const createFinanceApplication = (params: CreateFinanceApplicationParams) => {
  const { dispatch, financeApplicationParams, requestOptions } = params
  const { createFinanceApplication: createFn } = financeApplicationActions

  const updatedParams = mergeAndStringifyEntityObjects({}, financeApplicationParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response
    if (!success){
      return response
    }

    const { data } = response

    return {
      ...response,
      redirectUrl: digObject(data, 'data.external_url'),
    }
  })
}

type GenerateFinanceApplicationUrlParams = {
  dispatch: AppDispatch,
  financeApplication: FinanceApplicationModel,
  requestOptions?: FinanceApplicationRequestOptions,
}

const generateFinanceApplicationUrl = (params: GenerateFinanceApplicationUrlParams) => {
  const { dispatch, financeApplication, requestOptions } = params
  const { generateFinanceApplicationUrl: generateFn } = financeApplicationActions

  return dispatch(generateFn(financeApplication, requestOptions)).then((response) => {
    // @ts-expect-error TS2339
    const { success, data } = response
    if (!success){
      return response
    }

    return {
      ...response,
      redirectUrl: digObject(data, 'data.external_url'),
    }
  })
}

type UpdateFinanceApplicationParams = {
  dispatch: AppDispatch,
  financeApplication: FinanceApplicationModel,
  financeApplicationParams: Partial<FinanceApplicationModel>,
  requestOptions?: FinanceApplicationRequestOptions,
}

const updateFinanceApplication = (params: UpdateFinanceApplicationParams) => {
  const {
    dispatch, financeApplication, financeApplicationParams, requestOptions,
  } = params
  const { updateFinanceApplication: updateFn } = financeApplicationActions

  const updatedParams = {
    id: financeApplication.id,
    ...financeApplicationParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteFinanceApplicationParams = {
  dispatch: AppDispatch,
  financeApplication: DeleteParams<FinanceApplicationModel>,
}

const deleteFinanceApplication = (params: DeleteFinanceApplicationParams) => {
  const { dispatch, financeApplication } = params
  const { deleteFinanceApplication: deleteFn } = financeApplicationActions

  return dispatch(deleteFn(financeApplication))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useFinanceApplicationForm(
  financeApplication: Partial<FinanceApplicationModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const financeApplicationForm = useForm(
    defaultFormState,
    { entity: financeApplication, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [financeApplication.id, financeApplication.cache_key],
  )

  return {
    ...financeApplicationForm,
  }
}

export const useRelations = (financeApplication: Partial<FinanceApplicationModel> = {}) => {
  const { agency_payment_method_id } = financeApplication

  const { agencyPaymentMethods, paymentMethods } = useSelector(reduxState => reduxState.entities)

  const agencyPaymentMethod = agency_payment_method_id
  && agencyPaymentMethods[agency_payment_method_id] ? agencyPaymentMethods[agency_payment_method_id] : {}

  // @ts-expect-error TS2339
  const { payment_method_id } = agencyPaymentMethod || {}
  const paymentMethod = agency_payment_method_id && payment_method_id
    && paymentMethods[payment_method_id] ? paymentMethods[payment_method_id] : {}

  return {
    agencyPaymentMethod,
    paymentMethod,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: FinanceApplicationRequestOptions,
}

function useFinanceApplication(initEntity: Partial<FinanceApplicationModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: financeApplication }: { entity: FinanceApplicationModel} = useLatestEntity(initEntity, 'financeApplications')
  const { id } = financeApplication

  const dispatch = useDispatch()

  const { agencyPaymentMethod, paymentMethod } = useRelations(financeApplication)

  useReduxAction(
    'financeApplications',
    'loadFinanceApplication',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.financeApplications)

  const applicationStatus = getApplicationStatus(financeApplication)

  const { callbacks } = useContext(PageContext)

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  const { currentUser } = useCurrentUser()

  // Realtime/Websocket Connection
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: { payload: FinanceApplicationModel }) => createOrUpdateFinanceApplication({ data, dispatch }),
    },
    channelName: 'financeApplications',
    clientRef,
    eventName: 'financeApplication.createdOrUpdated.complete',
  }, [currentUser.id])

  return {
    agencyPaymentMethod,
    applicationStatus,
    callbacks: {
      createFinanceApplication: (
        financeApplicationParams: Partial<FinanceApplicationModel>,
        entityOptions?: FinanceApplicationRequestOptions,
      ) => (
        createFinanceApplication({ financeApplicationParams, dispatch, requestOptions: entityOptions })
      ),
      deleteFinanceApplication: () => deleteFinanceApplication({ dispatch, financeApplication }),
      generateFinanceApplicationUrl: (
        entityOptions?: FinanceApplicationRequestOptions,
      ) => generateFinanceApplicationUrl({
        dispatch, financeApplication, requestOptions: entityOptions,
      }),
      createFinanceApplicationModal: (campaign: CampaignModel) => launchModal({
        callbacks,
        modalKey: 'CreateFinanceApplicationModal',
        payload: { campaign },
      }),
      updateFinanceApplication: (
        financeApplicationParams: Partial<FinanceApplicationModel>,
        entityOptions?: FinanceApplicationRequestOptions,
      ) => (
        updateFinanceApplication({
          financeApplication, financeApplicationParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    financeApplication,
    loading: loading && !loadedIds.includes(financeApplication.id),
    paymentMethod,
    updating,
  }
}

export type UseFinanceApplicationPayload = ReturnType<typeof useFinanceApplication>

export default useFinanceApplication
